"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/core/components/auth/store/auth";
import {
  fetchAbAssignment,
  fetchScreenConfig,
  reportAbAssignment,
  type AbScreenAssignmentResponse,
} from "@/core/services/ab-experiments-report";
import {
  clearStoredVariant,
  getStoredVariant,
  setStoredVariant,
} from "@/core/utils/abTesting";
import {
  EXPERIMENTS_REGISTRY,
  type ExperimentConfig,
} from "./experiments-registry";

type ResolvedExperiment = {
  variant: string;
  enabled: boolean;
};

type ExperimentsContextValue = {
  getExperiment: (key: string) => ResolvedExperiment & { isReady: boolean };
  resolveScreen: (screenKey: string) => void;
  isScreenReady: (screenKey: string) => boolean;
};

const ExperimentsContext = createContext<ExperimentsContextValue | null>(null);

const experimentsByScreen = new Map<string, ExperimentConfig[]>();
const experimentByKey = new Map<string, ExperimentConfig>();

for (const exp of EXPERIMENTS_REGISTRY) {
  experimentByKey.set(exp.key, exp);
  const list = experimentsByScreen.get(exp.screenKey) ?? [];
  list.push(exp);
  experimentsByScreen.set(exp.screenKey, list);
}

async function resolveOneExperiment(
  config: ExperimentConfig,
  userId: string | undefined,
): Promise<ResolvedExperiment> {
  const { key, screenKey, fallback, knownVariants } = config;

  try {
    const rawStored = getStoredVariant(key);
    const validStored =
      rawStored && knownVariants.includes(rawStored) ? rawStored : null;
    if (rawStored && !validStored) {
      clearStoredVariant(key);
    }

    if (userId == null) {
      // Sem sessão: não há backend; só confiamos em localStorage se bater com knownVariants.
      return { variant: validStored ?? fallback, enabled: false };
    }

    const getRes = await fetchAbAssignment(key, screenKey);

    if (getRes && typeof getRes.variant === "string" && getRes.variant) {
      if (getRes.enabled !== false) {
        setStoredVariant(key, getRes.variant);
      }
      return { variant: getRes.variant, enabled: getRes.enabled !== false };
    }

    if (getRes && getRes.enabled === false) {
      return { variant: fallback, enabled: false };
    }

    const res = await reportAbAssignment(
      key,
      validStored ?? fallback,
      screenKey,
    );
    const data = res as AbScreenAssignmentResponse | null;
    if (
      data &&
      typeof data.enabled === "boolean" &&
      data.enabled &&
      data.variant
    ) {
      setStoredVariant(key, data.variant);
      return { variant: data.variant, enabled: true };
    }

    return { variant: fallback, enabled: false };
  } catch {
    // Rede indisponível: não reutilizar localStorage arbitrário; só fallback seguro.
    return { variant: config.fallback, enabled: false };
  }
}

/**
 * Resolves all experiments for a screen, checking the screen config first
 * so only "active" experiments are resolved via API. Paused/disabled
 * experiments get defaults (enabled: false).
 *
 * Variant selection comes from backend state (registered variants in Redis).
 * Frontend no longer needs hardcoded variant/weight lists.
 */
async function resolveScreenExperiments(
  screenKey: string,
  experiments: ExperimentConfig[],
  userId: string | undefined,
): Promise<{ key: string; result: ResolvedExperiment }[]> {
  let activeKeys: Set<string> | null = null;

  if (userId) {
    const config = await fetchScreenConfig(screenKey);
    if (config?.experiments) {
      activeKeys = new Set(
        config.experiments
          .filter((e) => e.status === "active")
          .map((e) => e.key),
      );
    }
  }

  return Promise.all(
    experiments.map(async (expConfig) => {
      if (activeKeys && !activeKeys.has(expConfig.key)) {
        return {
          key: expConfig.key,
          result: { variant: expConfig.fallback, enabled: false },
        };
      }
      const result = await resolveOneExperiment(expConfig, userId);
      return { key: expConfig.key, result };
    }),
  );
}

type ExperimentsState = {
  results: Record<string, ResolvedExperiment>;
  readyScreens: Set<string>;
};

function doResolveScreen(
  screenKey: string,
  pendingScreensRef: React.MutableRefObject<Set<string>>,
  userIdRef: React.MutableRefObject<string | undefined>,
  setState: React.Dispatch<React.SetStateAction<ExperimentsState>>,
) {
  if (pendingScreensRef.current.has(screenKey)) return;
  pendingScreensRef.current.add(screenKey);

  const experiments = experimentsByScreen.get(screenKey);
  if (!experiments?.length) {
    setState((prev) => ({
      ...prev,
      readyScreens: new Set(prev.readyScreens).add(screenKey),
    }));
    return;
  }

  resolveScreenExperiments(screenKey, experiments, userIdRef.current)
    .then((resolved) => {
      setState((prev) => {
        const newResults = { ...prev.results };
        for (const { key, result } of resolved) {
          newResults[key] = result;
        }
        return {
          results: newResults,
          readyScreens: new Set(prev.readyScreens).add(screenKey),
        };
      });
    })
    .catch(() => {
      setState((prev) => ({
        ...prev,
        readyScreens: new Set(prev.readyScreens).add(screenKey),
      }));
      pendingScreensRef.current.delete(screenKey);
    });
}

export function ExperimentsProvider({ children }: { children: ReactNode }) {
  const { userInfo } = useAuth();
  const userId =
    userInfo?.user?.id != null ? String(userInfo.user.id) : undefined;

  const [state, setState] = useState<ExperimentsState>({
    results: {},
    readyScreens: new Set(),
  });

  const pendingScreensRef = useRef(new Set<string>());
  const requestedScreensRef = useRef(new Set<string>());
  const userIdRef = useRef(userId);
  const prevUserIdRef = useRef(userId);

  userIdRef.current = userId;

  useEffect(() => {
    if (prevUserIdRef.current === userId) return;
    prevUserIdRef.current = userId;

    pendingScreensRef.current.clear();
    setState({ results: {}, readyScreens: new Set() });

    for (const screen of requestedScreensRef.current) {
      doResolveScreen(screen, pendingScreensRef, userIdRef, setState);
    }
  }, [userId]);

  const resolveScreen = useCallback((screenKey: string) => {
    requestedScreensRef.current.add(screenKey);
    doResolveScreen(screenKey, pendingScreensRef, userIdRef, setState);
  }, []);

  const value = useMemo<ExperimentsContextValue>(
    () => ({
      getExperiment: (key: string) => {
        const config = experimentByKey.get(key);
        if (!config) {
          return { variant: "unknown", enabled: false, isReady: true };
        }
        const resolved = state.results[key];
        if (resolved) {
          return { ...resolved, isReady: true };
        }
        return { variant: config.fallback, enabled: false, isReady: false };
      },
      resolveScreen,
      isScreenReady: (screenKey: string) => state.readyScreens.has(screenKey),
    }),
    [state, resolveScreen],
  );

  return (
    <ExperimentsContext.Provider value={value}>
      {children}
    </ExperimentsContext.Provider>
  );
}

/**
 * Returns the resolved value for a specific experiment.
 * Auto-triggers screen resolution if the experiment hasn't been resolved yet.
 */
export function useExperimentValue(
  experimentKey: string,
): ResolvedExperiment & { isReady: boolean } {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) {
    throw new Error(
      "useExperimentValue must be used inside <ExperimentsProvider>",
    );
  }

  const { getExperiment, resolveScreen } = ctx;
  const result = getExperiment(experimentKey);
  const config = experimentByKey.get(experimentKey);

  useEffect(() => {
    if (!result.isReady && config) {
      resolveScreen(config.screenKey);
    }
  }, [result.isReady, config, resolveScreen]);

  return result;
}

/**
 * Gate that blocks children rendering until all experiments
 * for the given screen are resolved. Shows neutral loading by default.
 */
export function ScreenExperimentsGate({
  screenKey,
  children,
  fallback,
}: {
  screenKey: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) {
    throw new Error(
      "ScreenExperimentsGate must be used inside <ExperimentsProvider>",
    );
  }

  const { isScreenReady, resolveScreen } = ctx;
  const ready = isScreenReady(screenKey);

  useEffect(() => {
    if (!ready) {
      resolveScreen(screenKey);
    }
  }, [ready, screenKey, resolveScreen]);

  if (!ready) {
    return (
      fallback ?? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
        </div>
      )
    );
  }

  return <>{children}</>;
}
